import { useState } from "react";
import type { CurrentResponse, HealthResponse } from "@shared/api";
import { useNow } from "@shared/hooks/useNow";
import { formatAgo, formatCountdown } from "@shared/lib/time";
import styles from "./AppHeader.module.css";

interface AppHeaderProps {
  current: CurrentResponse | null;
  health: HealthResponse | null;
  isRefreshing: boolean;
  lastRefreshAt: Date | null;
  onRefresh: () => void;
}

export function AppHeader({ current, health, isRefreshing, lastRefreshAt, onRefresh }: AppHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.brand}>
        <h1>Battlefield 6 · Personal Stats</h1>
        <HeaderMeta current={current} health={health} lastRefreshAt={lastRefreshAt} isRefreshing={isRefreshing} />
      </div>
      <div className={styles.actions}>
        <button
          className="icon-button"
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label="Refresh dashboard data"
          title="Refresh dashboard"
        >
          <span className={`icon-spin ${isRefreshing ? "is-spinning" : ""}`} aria-hidden="true">↻</span>
        </button>
        <NextPollCountdown health={health} />
      </div>
    </div>
  );
}

function HeaderMeta({
  current,
  health,
  lastRefreshAt,
  isRefreshing,
}: {
  current: CurrentResponse | null;
  health: HealthResponse | null;
  lastRefreshAt: Date | null;
  isRefreshing: boolean;
}) {
  const now = useNow(15_000);
  const polledAgo = health?.latest_poll_at ? formatAgo(health.latest_poll_at, now) : null;
  const statsAgo = current ? formatAgo(current.fetched_at, now) : "…";
  const refreshedAgo = lastRefreshAt ? formatAgo(Math.floor(lastRefreshAt.getTime() / 1000), now) : null;

  return (
    <div className={styles.sub}>
      {polledAgo && <span>Polled {polledAgo}</span>}
      {polledAgo && <span className={styles.subDot}>·</span>}
      <span>Stats updated {statsAgo}</span>
      {refreshedAgo && <span className={styles.subDot}>·</span>}
      {refreshedAgo && <span>Refreshed {refreshedAgo}</span>}
      {isRefreshing && <span className={styles.subDot}>·</span>}
      {isRefreshing && <span className={styles.pulseDot}>Refreshing</span>}
    </div>
  );
}

function NextPollCountdown({ health }: { health: HealthResponse | null }) {
  const now = useNow(1_000);
  const latestPollAt = health?.latest_poll_at ?? null;
  if (latestPollAt === null) {
    return (
      <div className="secondary-button fetch-button" aria-live="polite" title="Waiting for the first scheduled poll">
        Next poll soon
      </div>
    );
  }

  const nextPollAt = latestPollAt + 3 * 60;
  const secondsRemaining = nextPollAt - Math.floor(now / 1000);
  const overdue = secondsRemaining < 0;
  const label = overdue ? `Poll overdue by ${formatCountdown(Math.abs(secondsRemaining))}` : `Next poll in ${formatCountdown(secondsRemaining)}`;

  return (
    <div
      className={`secondary-button fetch-button ${overdue ? "is-stale" : ""}`}
      aria-live="polite"
      title="Battlefield stats are collected by a scheduled backend poll every 3 minutes"
    >
      <span aria-hidden="true">⏱</span>
      {label}
    </div>
  );
}

export function HealthPill({ health }: { health: HealthResponse }) {
  const [open, setOpen] = useState(false);
  const now = useNow(15_000);
  const stale = health.stale_poll;
  const label = health.latest_poll_at ? `${stale ? "Stale" : "Healthy"} · poll ${formatAgo(health.latest_poll_at, now)}` : "No polls";

  return (
    <div className={styles.healthPillWrap}>
      <button
        type="button"
        className={[styles.healthPill, stale ? styles.healthPillStale : ""].filter(Boolean).join(" ")}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className={[styles.healthDot, stale ? styles.healthDotStale : ""].filter(Boolean).join(" ")} aria-hidden="true" />
        <span>{label}</span>
        <span className={styles.healthChevron} aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className={styles.healthPanelPop} role="dialog" aria-label="Health details">
          <HealthItem label="Poll" value={health.latest_poll_at ? formatAgo(health.latest_poll_at, now) : "No polls"} />
          <HealthItem label="Snapshot" value={health.latest_snapshot_at ? formatAgo(health.latest_snapshot_at, now) : "No snapshots"} />
          <HealthItem label="Latest match" value={health.latest_match_at ? formatAgo(health.latest_match_at, now) : "No matches"} />
          <HealthItem
            label="Rows"
            value={`${health.snapshot_count.toLocaleString()} snapshots · ${health.match_count.toLocaleString()} matches · ${health.daily_rollup_count.toLocaleString()} days`}
          />
          <HealthItem
            label="Enrichment"
            value={`${health.latest_snapshot_has_gamemodes ? "Modes ✓" : "Modes ✗"} · ${health.latest_snapshot_has_maps ? "Maps ✓" : "Maps ✗"} · ${health.latest_snapshot_has_weapons ? "Weapons ✓" : "Weapons ✗"}`}
          />
          <HealthItem label="Schema" value={`v${health.schema_version}`} />
        </div>
      )}
    </div>
  );
}

function HealthItem({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.healthItem}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
